import React from "react";
import get from "lodash/get";
import sumBy from "lodash/sumBy";
import Box from "@mui/material/Box";
import orderBy from "lodash/orderBy";
import Grid from "@mui/material/Grid";
import useMount from "react-use/lib/useMount";
import { MFATable } from "app/components/table";
import { useCMSData } from "app/hooks/useCMSData";
import { PageLoader } from "app/components/pageLoader";
import { CanonicalUrl } from "app/components/canonicalUrl";
import { LegendPanel } from "app/pages/common/legendPanel";
import { useNavigate, useLocation } from "react-router-dom";
import { SunburstChart } from "app/components/charts/sunburst";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { SunburstDataItem } from "app/components/charts/sunburst/data";
import { LegendPanelItemProps } from "app/pages/common/legendPanel/data";
import { getAPIFormattedFilters } from "app/utils/getAPIFormattedFilters";
import { getNameFieldBasedOnLang } from "app/utils/getNameFieldBasedOnLang";

export function SectorsTabView() {
  const location = useLocation();
  const navigate = useNavigate();
  const cmsData = useCMSData({ returnData: true });

  const [selectedItem, setSelectedItem] = React.useState<{
    dataIndex: number;
    item: SunburstDataItem;
  } | null>(null);

  const appliedFilters = useStoreState((state) => state.appliedFilters);
  const fetchData = useStoreActions((actions) => actions.sectorsSunburst.fetch);
  const data = useStoreState(
    (state) =>
      get(
        state.sectorsSunburst,
        "data.vizData.children",
        []
      ) as SunburstDataItem[]
  );
  const loading = useStoreState((state) => state.sectorsSunburst.loading);
  const vizOrTable = useStoreState((state) => state.vizOrTable.value);
  const currentLanguage = useStoreState(
    (state) => state.selectedLanguage.value as string
  );

  const chart = React.useMemo(() => {
    return (
      <SunburstChart
        data={data}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
      />
    );
  }, [data, selectedItem]);

  const totalValue = React.useMemo(() => {
    if (selectedItem) {
      return selectedItem.item.value;
    }
    return sumBy(data, (item) => item.value);
  }, [data, selectedItem]);

  const legendItems = React.useMemo(() => {
    let items = orderBy(data, "value", "desc");
    if (selectedItem) {
      items = orderBy(selectedItem.item.children, "value", "desc");
      if (items.length === 0) {
        items = [selectedItem.item];
      }
    }
    items.forEach((item: SunburstDataItem) => {
      if (item.children) {
        item.children = orderBy(item.children, "value", "desc");
        item.children.forEach((subItem: SunburstDataItem) => {
          if (subItem.children) {
            subItem.children = orderBy(subItem.children, "value", "desc");
            subItem.children.forEach((subSubItem: SunburstDataItem) => {
              if (subSubItem.children) {
                subSubItem.children = orderBy(
                  subSubItem.children,
                  "value",
                  "desc"
                );
              }
            });
          }
        });
      }
    });
    return items.map((item: any) => ({
      label: item[getNameFieldBasedOnLang(currentLanguage)],
      value: item.value,
      color: item.color,
      subItems: item.children?.map((subItem: any) => ({
        label: subItem[getNameFieldBasedOnLang(currentLanguage)],
        value: subItem.value,
        color: subItem.color,
        subItems: subItem.children?.map((subSubItem: any) => ({
          label: subSubItem[getNameFieldBasedOnLang(currentLanguage)],
          value: subSubItem.value,
          color: subSubItem.color,
        })),
      })),
    }));
  }, [data, selectedItem, currentLanguage]);

  const flattenData = React.useMemo(() => {
    let flatten: any[] = [];
    data.forEach((item) => {
      flatten.push(item);
      if (item.children) {
        item.children.forEach((child) => {
          flatten.push(child);
          if (child.children) {
            child.children.forEach((grandChild) => {
              flatten.push(grandChild);
              if (grandChild.children) {
                grandChild.children.forEach((grandGrandChild) => {
                  flatten.push(grandGrandChild);
                });
              }
            });
          }
        });
      }
    });
    return flatten;
  }, [data]);

  const tableData = React.useMemo(() => {
    return data.map((item: any) => ({
      name: {
        label: item[getNameFieldBasedOnLang(currentLanguage)],
        link: `/${currentLanguage}/sectors/${item.code}`,
      },
      code: item.code,
      value: item.value,
      commitment: item.committed,
      children: item.children?.map((subItem: any) => ({
        name: {
          label: subItem[getNameFieldBasedOnLang(currentLanguage)],
          link: `/${currentLanguage}/sectors/${subItem.code}`,
        },
        code: subItem.code,
        value: subItem.value,
        commitment: subItem.committed,
        children: subItem.children?.map((subSubItem: any) => ({
          name: {
            label: subSubItem[getNameFieldBasedOnLang(currentLanguage)],
            link: `/${currentLanguage}/sectors/${subSubItem.code}`,
          },
          code: subSubItem.code,
          value: subSubItem.value,
          commitment: subSubItem.committed,
        })),
      })),
    }));
  }, [data, currentLanguage]);

  const tableColumns = React.useMemo(() => {
    return [
      {
        id: "name",
        label: "Category / DAC3 / DAC5",
      },
      {
        id: "code",
        label: "Code",
      },
      {
        id: "value",
        label: get(cmsData, "viz.disbursementsamount", "Total disbursements"),
      },
      {
        id: "commitment",
        label: get(cmsData, "viz.commitmentsamount", "Total commitment"),
      },
    ];
  }, [cmsData?.viz]);

  function onLegendItemClick(item: LegendPanelItemProps) {
    const index = flattenData.findIndex((d) => d.name === item.label);
    if (index >= 0) {
      if (item.subItems) {
        setSelectedItem({
          dataIndex: index,
          item: flattenData[index],
        });
      } else {
        navigate(
          `/${currentLanguage}/sectors/${flattenData[index].code}${location.search}`
        );
      }
    }
  }

  const view = React.useMemo(() => {
    return (
      <React.Fragment>
        <CanonicalUrl canonicalUrl="/sectors" />
        {loading && <PageLoader />}
        {vizOrTable === "table" && (
          <MFATable
            rows={tableData}
            defaultSortKey="value"
            columns={tableColumns}
          />
        )}
        {vizOrTable === "viz" && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={8}>
                {chart}
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <LegendPanel
                  title={get(cmsData, "viz.budget", "Budget")}
                  value={totalValue}
                  items={legendItems}
                  onItemClick={onLegendItemClick}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </React.Fragment>
    );
  }, [
    vizOrTable,
    tableData,
    chart,
    selectedItem,
    totalValue,
    legendItems,
    onLegendItemClick,
  ]);

  useMount(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  });

  React.useEffect(() => {
    const filters = getAPIFormattedFilters(appliedFilters);
    fetchData({
      values: {
        filters,
      },
    });
  }, [appliedFilters]);

  return view;
}
